import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/modules/user/user";

export type ShortFilterParams = {
  authorUuid?: string | null;
  date?: Date | null;
};

type GroupFilters = {
  authorUuid: string | null;
  dateFilter: Date | null;
};

/**
 * Short filter store for managing filter state
 * Filters are set in the store and trigger automatic refilter
 * Date and author filters can be loaded from URL query params
 */
export const useShortFilterStore = defineStore("shortFilter", () => {
  const router = useRouter();
  const userStore = useUserStore();

  /* Per-group filter storage */
  const filtersByGroup = ref<Record<string, GroupFilters>>({});

  /* Computed - active group ID from user store */
  const activeGroupId = computed(() => userStore.activeGroup);

  /* Helper to ensure group exists */
  function ensureGroupFilters(groupId: string): GroupFilters {
    if (!filtersByGroup.value[groupId]) {
      filtersByGroup.value[groupId] = {
        authorUuid: null,
        dateFilter: null,
      };
    }
    return filtersByGroup.value[groupId]!;
  }

  /* Computed property for current group's author filter */
  const authorUuid = computed({
    get: () =>
      activeGroupId.value
        ? ensureGroupFilters(activeGroupId.value).authorUuid
        : null,
    set: (value) => {
      if (activeGroupId.value) {
        ensureGroupFilters(activeGroupId.value).authorUuid = value;
      }
    },
  });

  /* Computed property for current group's date filter */
  const dateFilter = computed({
    get: () =>
      activeGroupId.value
        ? ensureGroupFilters(activeGroupId.value).dateFilter
        : null,
    set: (value) => {
      if (activeGroupId.value) {
        ensureGroupFilters(activeGroupId.value).dateFilter = value;
      }
    },
  });

  /* Update Author Filter */
  function setAuthorFilter(uuid: string | null): void {
    authorUuid.value = uuid;
    if (uuid) {
      dateFilter.value = null;
    }
  }

  /* Update Date Filter */
  function setDateFilter(date: Date | null): void {
    dateFilter.value = date;
    if (date) {
      authorUuid.value = null;
    }
  }

  /* Clear all filters */
  function clearAllFilters(): void {
    authorUuid.value = null;
    dateFilter.value = null;
  }

  /* Check if any filters are active */
  function hasActiveFilters(): boolean {
    return authorUuid.value !== null || dateFilter.value !== null;
  }

  /* Get current filter params */
  function getFilterParams(): ShortFilterParams {
    return {
      authorUuid: authorUuid.value,
      date: dateFilter.value,
    };
  }

  /* Sync filters from URL query on page load */
  function loadFiltersFromUrl(): void {
    if (!activeGroupId.value) return;

    const route = router.currentRoute.value;
    const filters = ensureGroupFilters(activeGroupId.value);

    // Load date from query params (e.g. ?date=YYYY-MM-DD)
    if (route.query?.date && typeof route.query.date === "string") {
      const dateObj = new Date(route.query.date);
      if (!isNaN(dateObj.getTime())) {
        filters.dateFilter = dateObj;
        filters.authorUuid = null;
        return;
      }
    }

    // Load author from query params (e.g. ?author=uuid)
    const authorParam = route.query?.author;
    if (authorParam && typeof authorParam === "string") {
      filters.authorUuid = authorParam;
      filters.dateFilter = null;
    }
  }

  return {
    // State
    authorUuid,
    dateFilter,

    // Computed
    activeGroupId,

    // Actions
    setAuthorFilter,
    setDateFilter,
    clearAllFilters,
    hasActiveFilters,
    getFilterParams,
    loadFiltersFromUrl,
  };
});
